import { useQuery } from '@tanstack/react-query';
import { mintAttachmentDownloadUrl } from '../services/api/comments';

/**
 * Phase 25 Plan 25-05 — lazy signed-URL mint for attachment downloads.
 *
 * Server-side TTL is 1 hour (Phase 22 D-10); we set staleTime to 50 minutes so
 * a follow-up render reuses the URL without re-minting (saves a roundtrip and
 * an audit-log noise pulse if signed-URL minting ever grows audit coverage).
 *
 * `enabled` is controlled by the caller — for image MIMEs the AttachmentRow
 * enables fetch immediately so the thumbnail can render; for non-images the
 * AttachmentRow enables fetch on click so the initial list load stays light
 * (D-20).
 *
 * Quick task 260810-lpq — optional fifth positional `disposition` param.
 * Passing `'attachment'` mints a save-forcing URL (the explicit download
 * control, LPQ-01), distinct from the default view URL. It's included in the
 * queryKey so the two cache independently — a view mint never satisfies a
 * download mint or vice versa.
 */
export function useAttachmentDownloadUrl(
  taskId: number,
  commentId: number,
  attachmentId: number,
  enabled: boolean,
  disposition?: 'attachment',
) {
  return useQuery<{ url: string; expiresAt: string }>({
    queryKey: ['attachment-url', taskId, commentId, attachmentId, disposition ?? 'view'],
    queryFn: () => mintAttachmentDownloadUrl(taskId, commentId, attachmentId, disposition),
    enabled,
    staleTime: 50 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
